import "./site-config/tkees-debug-log"; // Trick to get a log in the console before any other imports

import { RedoClient } from "@redotech/redo-api-client";
import { contactDataFormat } from "@redotech/redo-customer-common/customer-contact-definition";
import { isCoveragePausedAt } from "@redotech/redo-return-coverage-common/coverage-calculator";
import {
  CART_ATTRIBUTE_KEY,
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "@redotech/redo-return-warranties-common/extended-warranty-enable-criteria";
import {
  ANONYMOUS_ID_CART_ATTRIBUTE_KEY,
  ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY,
} from "@redotech/redo-shopify-common/cart";
import {
  AttachmentStrategy,
  doesAttachmentStrategyRenderInCart,
} from "@redotech/redo-storefront-common/attachment-strategy";
import {
  FeesErrorDescription,
  SilentExpectedErrors,
} from "@redotech/redo-storefront-common/config/errors";
import { getStorefrontTrackingConfigFromAutocheck } from "@redotech/redo-storefront-tracking/config";
import { BasicEventsProvider } from "@redotech/redo-storefront-tracking/events-provider/basic-events-provider";
import { REDO_CART_TOKEN_STORAGE_KEY } from "@redotech/redo-storefront-web/cache/keys";
import {
  applyTreatment,
  pickTreatment,
} from "@redotech/redo-storefront-web/config/handle-split-testing";
import { Treatment } from "@redotech/redo-storefront-widget-common/split";
import { showCoverageBasedOnBillingStatus } from "@redotech/redo-storefront-widget-common/widget";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import {
  getSessionStorageWithExpiry,
  setSessionStorageWithExpiry,
} from "@redotech/redo-web/utils/session-storage-wrapper";
import {
  ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
  ANONYMOUS_SHOPPER_ID_KEY,
  COVERAGE_TREATMENT_ID_QUERY_PARAM,
  CUSTOMER_EMAIL,
  CUSTOMER_PHONE,
} from "@redotech/redo-web/utils/shared-conf";
import {
  ShopifyAjaxClient,
  ShopifyProducts,
} from "@redotech/shopify-client/ajax";
import { storeTransform } from "@redotech/util/store";
import { sessionStore } from "@redotech/web-util/storage";
import { getAutocheckCache } from "./autocheck-cache";
import { createExtensionShopifyClient } from "./client/client-factory";
import { CommercecloudStorefrontClient } from "./client/commercecloud-storefront";
import {
  ShopifyExtensionClient,
  trackUserSession,
} from "./client/shopify-extension-client";
import { StorefrontClient } from "./client/storefront";
import { CONCIERGE_PRODUCT_FORM_CONTAINER_ID } from "./concierge/pre-init";
import { getInternalAppStatusEmitter, getRedoStorefrontConfig } from "./config";
import { inCoverageBuilderMode } from "./coverage-builder/coverage-builder-init";
import { waitForCoverageBuilderAutocheck } from "./coverage-builder/preview-push";
import { shouldInitializeEstimatedDeliveryDate } from "./estimated-delivery-date/gate";
import { getFeesCache } from "./fees-cache";
import { isHsaEnabled, isHsaSkipShopPayEnabled } from "./hsa/pre-init";
import { isMarketingEmbeddedFormEnabled } from "./marketing-embedded-form/pre-init";
import { log, mountRedoDebugFunction } from "./monitoring/log";
import {
  datadogLog,
  executeAndMonitor,
  initStorefrontLogging,
} from "./monitoring/monitor";
import { initializeTiming, reportTiming } from "./monitoring/timing";
import {
  initializeIfOrderTrackingLoginFound,
  isOrderTrackingLoginEnabled,
} from "./order-tracking-login/pre-init";
import { checkForPlacementTestMode } from "./placement-test/token-utils";
import { shouldShowPreviewModeUi } from "./preview-mode/pre-init";
import { PreviewModeState } from "./preview-mode/preview-state";
import { shouldRenderRedoStorefrontOverlay } from "./redo-storefront/should-render-overlay";
import {
  initializeIfReviewsElementsFound,
  isReviewsEnabled,
} from "./reviews/pre-init";
import { inSelectorPickerMode } from "./selector-picker/selector-picker-init";
import { isOnProductPage } from "./shopify";
import {
  getThemePublishedStatus,
  isTkeesDebugTheme,
  ThemePublishedStatus,
} from "./site-config/theme";
import {
  CoverageNotShowingReason,
  feesErrorToCoverageNotShowingReason,
  OPEN_SEQUENCE,
} from "./storefront-debugger/coverage-debug-init";
import { passesStorefrontPublishGate } from "./storefront-publish";
import { isTryFirstSetup } from "./try-first/pre-init";
import { getLocalStorageIgnoreExpiry, getStorefrontAccessToken } from "./utils";
import { setInitialCartAttributes } from "./utils/cart";
import { getRedoProductsCache } from "./utils/redo-products-cache";
import { enableSkipShopPay } from "./utils/skip-shop-pay";

if (isTkeesDebugTheme()) {
  console.log("[Tkees test theme] index.ts imports completed.");
}

// This is fine to run twice
if (location.href.includes("/completeExchange")) {
  const url = new URL(location.href);
  url.pathname = "/apps/redo" + url.pathname;
  location.href = url.toString();
}

// Latest cart token, refreshed as the cart changes; lets cart mutations target the live cart.
const getFreshCartToken = (): string | undefined =>
  getSessionStorageWithExpiry(REDO_CART_TOKEN_STORAGE_KEY);

// Place all code inside this function to ensure Redo is only initialized once
const initializeRedoStorefrontSubmodules = () => {
  if (!globalThis.redoWidgetId) {
    const scriptUrl = new URL(
      (document.currentScript as HTMLScriptElement).src,
      location.href,
    );

    const params = new URLSearchParams(
      new URL(scriptUrl, location.href).search,
    );

    globalThis.redoWidgetId =
      globalThis.redoWidgetId ?? params.get("widget_id");
  }

  // Initialize Redo storefront configuration
  getRedoStorefrontConfig();

  // Initialize internal app status emitter
  getInternalAppStatusEmitter();

  const exchangeSelectStore = storeTransform(
    sessionStore("redo.exchange-select"),
    {
      read: (string) => string !== undefined,
      write: (value) => (value ? "" : undefined),
    },
  );

  if (!localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY)) {
    localStorage.setItem(ANONYMOUS_SHOPPER_ID_KEY, crypto.randomUUID());
  }

  // Initialize timing tracking
  initializeTiming();

  // Check for placement test mode FIRST, before any other initialization
  void executeAndMonitor(async () => {
    const testConfig = checkForPlacementTestMode();

    if (testConfig) {
      console.log("Placement test mode detected, initializing configurator...");

      // Block normal widget initialization when in test mode
      // MUST be set BEFORE async import to prevent race condition with widget initialization
      globalThis.blockRedo = true;

      // Dynamically import the initializer to keep main bundle small
      const { initializePlacementConfigurator } =
        await import("./placement-test/initializer");

      initializePlacementConfigurator(testConfig);
      return;
    }
  });

  // coverage builder
  void executeAndMonitor(async () => {
    if (!inCoverageBuilderMode()) {
      return;
    }

    const { initializeCoverageBuilderStorefront } =
      await import("./coverage-builder/coverage-builder-storefront");
    initializeCoverageBuilderStorefront();
  });

  // generic selector picker
  void executeAndMonitor(async () => {
    if (!inSelectorPickerMode()) {
      return;
    }

    const { initializeSelectorPickerStorefront } =
      await import("./selector-picker/selector-picker-storefront");
    initializeSelectorPickerStorefront();
  });

  // coverage debug popup
  void executeAndMonitor(async () => {
    let sequence = "";

    const loadAndInitializeCoverageDebugPopup = async () => {
      const { initializeCoverageDebugPopup } =
        await import("./storefront-debugger/coverage-debug");
      initializeCoverageDebugPopup();
    };

    const handleKeydown = (event: KeyboardEvent) => {
      sequence = (sequence + event.key).slice(-OPEN_SEQUENCE.length);

      if (sequence === OPEN_SEQUENCE) {
        void loadAndInitializeCoverageDebugPopup();

        window.removeEventListener("keydown", handleKeydown);
      }
    };
    window.addEventListener("keydown", handleKeydown);

    return;
  });

  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    const autocheck = await getAutocheckCache();

    try {
      initStorefrontLogging({ autocheck });
    } catch (error) {
      console.error("Error initializing autocheck:", error);
    }
  });

  void executeAndMonitor(async () => {
    // wait a few seconds to make sure page has loaded.
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const honeypot = document.querySelector("#redo_shopify_honeypot");
    if (honeypot) {
      datadogLog("info", "found redo_shopify_honeypot on merchant's site");
    }
  });

  // Merchant custom JavaScript
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    const autocheck = await getAutocheckCache();
    if (!autocheck?.customJs?.enabled || !autocheck.customJs.script) {
      return;
    }
    const script = document.createElement("script");
    script.id = "redo-custom-js";
    script.textContent = autocheck.customJs.script;
    document.head.append(script);
  });

  // Extended Warranty
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }

    const autocheck = await getAutocheckCache();

    if (autocheck?.extendedWarranties?.enabled) {
      log(
        "Extended warranties enabled. Will get client and check display settings.",
      );
      const ajaxClient = ShopifyAjaxClient.fromEnv();
      try {
        const cart = await ajaxClient.cartGet();
        // Initialize the cart attribute if it doesn't exist
        if (!cart?.attributes?.[CART_ATTRIBUTE_KEY]) {
          await ajaxClient.updateCartAttribute({
            attributes: [
              { key: CART_ATTRIBUTE_KEY, value: JSON.stringify({}) },
            ],
          });
        }
      } catch (error) {
        log("Error initializing cart attribute", error);
      }

      const extensionShopifyClient = new StorefrontClient(
        globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost ??
          globalThis.redoStorefront?.storeUrl ??
          "",
        getStorefrontAccessToken(autocheck) ?? "",
        globalThis?.Shopify?.Checkout?.cartToken ||
          (await ajaxClient.cartGet()).token!,
        getFreshCartToken,
      );

      // TODO: When these are added to ab testing, move inside the EwSettingsProvider
      const cartToggleExperience =
        autocheck?.extendedWarranties?.cartToggleExperience;
      if (
        cartToggleExperience &&
        cartToggleExperience !== ExtendedWarrantyCartToggleExperience.DISABLED
      ) {
        log(
          "Extended warranties cart toggle experience is configured. Will initialize cart toggle.",
        );
        try {
          const { initialize: initializeExtendedWarranty } =
            await import("./extended-warranty/cart-toggle/initializer");
          initializeExtendedWarranty(extensionShopifyClient, autocheck);
        } catch (error: any) {
          console.error("Error initializing extended warranty", error);
        }
      }
      // TODO: When these are added to ab testing, move inside the EwSettingsProvider
      const pdpRenderMethod = autocheck?.extendedWarranties?.pdpRenderMethod;
      if (pdpRenderMethod === ExtendedWarrantyPdpRenderMethod.SCRIPT_TAG) {
        log(
          "Extended warranties PDP render method is configured. Will initialize PDP options.",
        );
        try {
          const { initialize: initializeExtendedWarranty } =
            await import("./extended-warranty/pdp-options/initializer");
          initializeExtendedWarranty(extensionShopifyClient, autocheck);
        } catch (error: any) {
          console.error("Error initializing extended warranty", error);
        }
      }
      const popupExperience = autocheck?.extendedWarranties?.popupExperience;
      if (
        popupExperience &&
        popupExperience !== ExtendedWarrantyPopupExperience.DISABLED
      ) {
        log(
          "Extended warranties popup experience is configured. Will initialize popup toggle.",
        );
        try {
          const { initialize: initializeExtendedWarranty } =
            await import("./extended-warranty/popup-toggle/initializer");
          initializeExtendedWarranty(extensionShopifyClient, autocheck);
        } catch (error: any) {
          console.error("Error initializing extended warranty", error);
        }
      }
    }
  });

  // Marketing embedded form
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    const autocheck = await getAutocheckCache();

    if (!isMarketingEmbeddedFormEnabled({ autocheck })) {
      return;
    }
    if (!passesStorefrontPublishGate(autocheck, "marketing")) {
      return;
    }

    log("Initializing marketing embedded form");

    try {
      const { initialize: initializeMarketingEmbeddedForm } =
        await import("./marketing-embedded-form/marketing-embedded-form");
      initializeMarketingEmbeddedForm(autocheck);
    } catch (error: any) {
      console.error("Error initializing marketing embedded form:", error);
    }
  });

  // Order tracking login (div-based embed). Pre-init keeps the import cost
  // off the wire for merchants who haven't enabled order tracking AND for
  // pages that don't host the target div.
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    const autocheck = await getAutocheckCache();
    if (!isOrderTrackingLoginEnabled(autocheck)) {
      return;
    }
    await initializeIfOrderTrackingLoginFound();
  });

  // Review stars
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }
    const autocheck = await getAutocheckCache();
    if (!isReviewsEnabled(autocheck)) {
      return;
    }
    if (!passesStorefrontPublishGate(autocheck, "reviews")) {
      return;
    }

    log("Initializing reviews stars");

    try {
      await initializeIfReviewsElementsFound(autocheck); // Use dynamic imports inside the function to keep bundle small even though statically imported here.
    } catch (error: any) {
      console.error("Error initializing review stars:", error);
    }
  });

  // Try first
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }
    const autocheck = await getAutocheckCache();
    reportTiming("tryFirst.autocheckCacheRetrieved");

    try {
      if (!isTryFirstSetup(autocheck)) {
        return;
      }

      const { initialize: initializeTryFirst } =
        await import("./try-first/initializer");
      reportTiming("tryFirst.initializeFunctionCalled");

      const ajaxClient = ShopifyAjaxClient.fromEnv();
      const cart = await ajaxClient.cartGet();
      const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
      const shop =
        globalThis.Shopify?.shop ??
        globalThis.Shopify?.Checkout?.apiHost ??
        globalThis.redoStorefront?.storeUrl;

      if (!shop || !cartToken) {
        return;
      }

      const storefrontClient = new StorefrontClient(
        shop,
        getStorefrontAccessToken(autocheck),
        cartToken,
        getFreshCartToken,
      );
      initializeTryFirst(autocheck, storefrontClient);
    } catch (error: any) {
      console.error("Error initializing try first:", error);
    }
  });

  // Cart optimization
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }
    const autocheck = await getAutocheckCache();

    try {
      const {
        isCartOptimizationEnabled,
        initialize: initializeCartOptimization,
      } = await import("./cart-optimization/initializer");

      if (!isCartOptimizationEnabled(autocheck)) {
        return;
      }

      const ajaxClient = ShopifyAjaxClient.fromEnv();
      const cart = await ajaxClient.cartGet();
      const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
      const shop =
        globalThis.Shopify?.shop ?? globalThis.Shopify?.Checkout?.apiHost;

      if (!shop || !cartToken) {
        return;
      }

      const storefrontClient = new StorefrontClient(
        shop,
        getStorefrontAccessToken(autocheck),
        cartToken,
        getFreshCartToken,
      );
      initializeCartOptimization(autocheck, storefrontClient);
    } catch (error: any) {
      console.error("Error initializing cart optimization:", error);
    }
  });

  // Loyalty page customer view
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }
    const autocheck = await getAutocheckCache();
    if (!passesStorefrontPublishGate(autocheck, "loyalty")) {
      return;
    }

    try {
      const { initialize: initializeLoyaltyPageCustomerView } =
        await import("./loyalty-page/loyalty-page-customer-view");
      initializeLoyaltyPageCustomerView(autocheck);
    } catch (error: any) {
      console.error("Error initializing loyalty page customer view:", error);
    }
  });

  // Redo storefront
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }

    const autocheck = await getAutocheckCache();

    if (!shouldRenderRedoStorefrontOverlay(autocheck)) {
      return;
    }

    const ajaxClient = ShopifyAjaxClient.fromEnv();
    const cart = await ajaxClient.cartGet();
    const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
    const shop =
      globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl;

    if (!shop || !cartToken) {
      console.error("Shop or cart token not found");
      return;
    }

    const storefrontClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
      getFreshCartToken,
    );

    try {
      const { initialize } = await import("./redo-storefront/index");
      initialize({
        autocheck,
        storefrontExtensionShopifyClient: storefrontClient,
      });
    } catch (error: any) {
      console.error("Error initializing redo storefront", error);
      (globalThis as any).redo_app_loaded_on_page = false;
    }
  });

  // Marketing popup form
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }

    const autocheck = await getAutocheckCache();

    const queryParameters = new URLSearchParams(location.search);
    const testForm = queryParameters.get("redo-signup-test");
    const testSpotlight = queryParameters.get("redo-spotlight-test");

    if (testSpotlight && !testForm) {
      return;
    }
    const shouldRender = autocheck.marketing.enabled || testForm;

    if (!shouldRender) {
      return;
    }
    if (!testForm && !passesStorefrontPublishGate(autocheck, "marketing")) {
      return;
    }

    try {
      const { initialize } =
        await import("./marketing-popup-form/marketing-popup-form");
      initialize(autocheck);
    } catch (error: any) {
      console.error("Error initializing marketing popup form", error);
    }
  });

  // Spotlight
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }

    const autocheck = await getAutocheckCache();

    const queryParameters = new URLSearchParams(location.search);
    const testSpotlight = queryParameters.get("redo-spotlight-test");
    const testForm = queryParameters.get("redo-signup-test");

    if (testForm && !testSpotlight) {
      return;
    }
    const shouldRender =
      autocheck.marketing.enabled ||
      autocheck.conversion.enabled ||
      testSpotlight;

    if (!shouldRender) {
      return;
    }
    if (
      !testSpotlight &&
      !passesStorefrontPublishGate(autocheck, "marketing") &&
      !passesStorefrontPublishGate(autocheck, "conversion")
    ) {
      return;
    }

    try {
      const { initialize } = await import("./spotlight/spotlight");
      initialize(autocheck);
    } catch (error: any) {
      console.error("Error initializing spotlight:", error);
    }
  });

  // HSA/FSA storefront surfaces. Gated via the dependency-light
  // `./hsa/pre-init` predicates so each surface's bundle loads only when
  // needed: the lightweight (React-free) skip-Shop-Pay checkout interception
  // when `autocheck.hsa.checkoutButtonsSkipShopPay` is set, and the heavy PDP
  // questionnaire modal (opened via `window.RedoHsa.open()`) when HSA is
  // enabled.
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }

    const autocheck = await getAutocheckCache();
    if (!isHsaEnabled({ autocheck })) {
      return;
    }
    if (!passesStorefrontPublishGate(autocheck, "hsa")) {
      return;
    }

    // Expire a stale per-cart payment-exclusivity flag: being back on the
    // storefront ends the "Buy with HSA" intent that set it.
    if (
      !globalThis.redoStorefront?.type ||
      globalThis.redoStorefront.type === "shopify"
    ) {
      try {
        const { installHsaExclusivityExpiry } =
          await import("./hsa/clear-stale-exclusivity");
        installHsaExclusivityExpiry(ShopifyAjaxClient.fromEnv());
      } catch (error: any) {
        console.error("Error installing HSA exclusivity expiry:", error);
      }
    }

    if (isHsaSkipShopPayEnabled({ autocheck })) {
      // Enable the shared flag synchronously, before the (dynamic) interception
      // import resolves, so Redo-owned checkout navigations in other init
      // blocks — the coverage `ensureProduct` re-click in `manager.tsx`, the
      // Checkout+ buttons, the rescue panel — read it reliably regardless of
      // bundle load timing.
      enableSkipShopPay();
      try {
        const { installSkipShopPayInterception } =
          await import("./hsa/checkout-skip-shop-pay");
        installSkipShopPayInterception();
      } catch (error: any) {
        console.error(
          "Error installing HSA skip-Shop-Pay interception:",
          error,
        );
      }
    }

    try {
      const { initialize } = await import("./hsa/initializer");
      await initialize(autocheck);
    } catch (error: any) {
      console.error("Error initializing HSA questionnaire:", error);
    }
  });

  // Conversion progress bar
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }

    const autocheck = await getAutocheckCache();

    if (!autocheck.conversion.enabled) {
      return;
    }
    if (!passesStorefrontPublishGate(autocheck, "conversion")) {
      return;
    }

    const progressBarConfig =
      autocheck.conversion.splitStorefrontFeatures?.progressBar ??
      autocheck.conversion.storefrontProgressBar;

    if (!progressBarConfig) {
      return;
    }

    const ajaxClient = ShopifyAjaxClient.fromEnv();
    const cart = await ajaxClient.cartGet();
    const cartToken = globalThis?.Shopify?.Checkout?.cartToken || cart.token;
    const shop =
      globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl;

    if (!shop || !cartToken) {
      console.error("Shop or cart token not found for progress bar");
      return;
    }

    const storefrontClient = new StorefrontClient(
      shop,
      getStorefrontAccessToken(autocheck),
      cartToken,
      getFreshCartToken,
    );

    try {
      const { initialize } =
        await import("./redo-storefront/conversion/progress-bar/progress-bar-initializer");
      initialize({ progressBarConfig, storefrontClient, autocheck });
    } catch (error: any) {
      console.error("Error initializing progress bars:", error);
    }
  });

  // Estimated delivery date (EDD) on SFCC/Salesforce Commerce Cloud checkout
  void executeAndMonitor(async () => {
    if (
      !(await shouldInitializeEstimatedDeliveryDate({
        blockRedo: globalThis.blockRedo,
        storefrontType: globalThis.redoStorefront?.type,
        isOnTargetPage: () => CommercecloudStorefrontClient.isOnCheckoutPage(),
        getAutocheck: getAutocheckCache,
      }))
    ) {
      return;
    }

    const autocheck = await getAutocheckCache();
    const { initializeEstimatedDeliveryDate } =
      await import("./estimated-delivery-date");
    initializeEstimatedDeliveryDate({
      suppressCartItemFields:
        autocheck.checkoutDeliveryEstimates.suppressCartItemFields,
    });
  });

  // Estimated delivery date (EDD) on the SFCC order-confirmation page
  void executeAndMonitor(async () => {
    if (
      !(await shouldInitializeEstimatedDeliveryDate({
        blockRedo: globalThis.blockRedo,
        storefrontType: globalThis.redoStorefront?.type,
        isOnTargetPage: () =>
          CommercecloudStorefrontClient.isOnConfirmationPage(),
        getAutocheck: getAutocheckCache,
      }))
    ) {
      return;
    }

    const autocheck = await getAutocheckCache();
    const { initializeConfirmationDeliveryDate } =
      await import("./estimated-delivery-date/confirmation");
    initializeConfirmationDeliveryDate({
      suppressConfirmationSelectors:
        autocheck.checkoutDeliveryEstimates.suppressConfirmationSelectors,
    });
  });

  // Estimated delivery date (EDD) on the SFCC account order-details page and the
  // order-history page (`.../orders`), where More Details expands an order inline.
  void executeAndMonitor(async () => {
    if (
      !(await shouldInitializeEstimatedDeliveryDate({
        blockRedo: globalThis.blockRedo,
        storefrontType: globalThis.redoStorefront?.type,
        isOnTargetPage: () =>
          CommercecloudStorefrontClient.isOnOrderDetailsPage() ||
          CommercecloudStorefrontClient.isOnOrderHistoryPage(),
        getAutocheck: getAutocheckCache,
      }))
    ) {
      return;
    }

    const { initializeOrderDetailsDeliveryDate } =
      await import("./estimated-delivery-date/order-details");
    initializeOrderDetailsDeliveryDate();
  });

  // Customer widget
  void executeAndMonitor(async () => {
    if (globalThis.blockRedo) {
      return;
    }
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }

    const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;

    const redoClient = new RedoClient(
      globalThis.redoStorefront?.env?.REDO_API_URL ?? process.env.REDO_API_URL!,
    );
    const widgetClient = new ShopifyExtensionClient(
      redoClient,
      globalThis.redoWidgetId,
      globalThis.Shopify?.shop ??
        globalThis.Shopify?.Checkout?.apiHost ??
        globalThis.redoStorefront?.storeUrl,
    );

    const autocheck = await getAutocheckCache();

    if (autocheck?.customerWidgetEnabled) {
      // On Shopify the script tag loads the widget; injecting here would double-download it.
      const scriptTagLoadsWidget =
        !!globalThis.Shopify && autocheck.chatWidgetScriptTagInstalled;
      if (!scriptTagLoadsWidget) {
        log("Adding customer widget script");
        const storeUrl =
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost ??
          globalThis.redoStorefront?.storeUrl;
        if (!globalThis.redoWidgetId && !storeUrl) {
          console.error("Widget ID or storeUrl required");
          return;
        }

        const CHAT_WIDGET_BASE =
          process.env.REDO_CHAT_WIDGET_URL || "https://chat-widget.getredo.com";

        const script = document.createElement("script");
        script.src = globalThis.redoWidgetId
          ? `${CHAT_WIDGET_BASE}/widget.js?widgetId=${globalThis.redoWidgetId}`
          : `${CHAT_WIDGET_BASE}/widget.js?storeUrl=${storeUrl}`;
        script.async = true;
        document.head.append(script);
      }

      if (
        autocheck.concierge.placeConciergeProductForm &&
        (document.querySelector(CONCIERGE_PRODUCT_FORM_CONTAINER_ID) ||
          isOnProductPage(onTapcart))
      ) {
        const { addConciergeProductForm } = await import("./product-page");
        addConciergeProductForm(autocheck, onTapcart);

        const { initialize } = await import("./concierge/initializer");
        initialize({
          fromThemeExtension: true,
          autocheckSettings: autocheck.concierge.conciergeProductFormSettings,
        });
      }

      // concierge enabled when support.settings.ai.enabled is true
      if (autocheck.customerAccounts.enabled || autocheck.concierge.enabled) {
        try {
          await trackUserSession(widgetClient);
        } catch (error: any) {
          console.error("Error sending user session", error);
        }
      }
    }
  });

  // Storefront coverage, exchange select
  void executeAndMonitor(async () => {
    if (globalThis.redoStorefront?.type === "commentsold") {
      return;
    }
    const onTapcart = !!globalThis.Tapcart && !globalThis.Shopify;
    reportTiming("coverage.beforeAutocheckCacheRetrieved");
    // In builder preview the host's unsaved autocheck is the source of truth;
    // wait for its first push so init can't race caches it will override.
    const builderAutocheck = inCoverageBuilderMode()
      ? await waitForCoverageBuilderAutocheck()
      : undefined;
    const autocheck = builderAutocheck ?? (await getAutocheckCache());
    reportTiming("coverage.autocheckCacheRetrieved");

    if (
      globalThis.redoStorefront?.type === "commercecloud" ||
      globalThis.redoStorefront?.type === "bigcommerce"
    ) {
      const shouldStartExchange =
        location.search.includes("redoExchangeSelect");
      if (shouldStartExchange) {
        exchangeSelectStore.set(true);
      }
    }
    if (exchangeSelectStore.get()) {
      const { initialize } = await import("./exchange-select");
      await initialize(autocheck, onTapcart);
      return;
    }

    if (globalThis.blockRedo) {
      if (globalThis.redoStorefront) {
        globalThis.redoStorefront.coverageNotShowingReason = "BLOCK_REDO";
      }
      return;
    }

    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlID = urlParams.get(COVERAGE_TREATMENT_ID_QUERY_PARAM);
    const onPreview = PreviewModeState.getActiveAndSet();

    // Set global preview mode flag to bypass restrictions
    if (onPreview) {
      console.log("📸 Redo is in preview mode");
    }

    // Storefront publish-status kill switch for the entire returns-coverage
    // family. Coverage, package protection, and final sale returns share a
    // single "Returns coverage" publish status; if it isn't live for the
    // current theme, none of the returns-coverage code below runs (the manager
    // never mounts, no fees/cart calls happen). This is the high-level gate —
    // it short-circuits before any coverage work. Preview/builder bypasses it.
    if (
      !passesStorefrontPublishGate(autocheck, "coverage") &&
      !inCoverageBuilderMode()
    ) {
      if (globalThis.redoStorefront) {
        globalThis.redoStorefront.coverageNotShowingReason =
          CoverageNotShowingReason.STOREFRONT_PUBLISH_STATUS;
      }
      log(
        "⛔️ Redo storefront coverage disabled - storefront publish status is not live for this theme",
      );
      return;
    }

    const localStorageTreatmentId =
      getLocalStorageIgnoreExpiry("redo_treatment_id");

    let selectedTreatment: Treatment | undefined;
    if (autocheck.treatments) {
      selectedTreatment = pickTreatment(
        autocheck.treatments,
        localStorageTreatmentId,
        urlID,
      );
    }
    reportTiming("coverage.beforeFeesCacheRetrieved");
    const fees =
      globalThis.redoStorefront?.previewFees ?? (await getFeesCache());
    if ("error" in fees) {
      if (
        typeof fees.error === "string" &&
        SilentExpectedErrors.includes(fees.error as FeesErrorDescription)
      ) {
        if (globalThis.redoStorefront) {
          const reason =
            feesErrorToCoverageNotShowingReason[
              fees.error as FeesErrorDescription
            ];
          globalThis.redoStorefront.coverageNotShowingReason = reason;
        }
        log("Error in fees endpoint", { error: fees.error });
      } else {
        console.error("error in fees endpoint", { error: fees.error });
        if (globalThis.redoStorefront) {
          globalThis.redoStorefront.coverageNotShowingReason = "FEES_ERROR";
        }
      }
      return;
    }
    reportTiming("coverage.afterFeesCacheRetrieved");

    if (autocheck.attachmentStrategy === AttachmentStrategy.NONE) {
      log(
        "⛔️ Redo storefront extension disabled - attachment strategy is None (API / custom only)",
      );
      if (globalThis.redoStorefront) {
        globalThis.redoStorefront.coverageNotShowingReason =
          CoverageNotShowingReason.NONE_STRATEGY;
      }
      return;
    }

    const storefrontClient = await createExtensionShopifyClient(
      autocheck,
      onTapcart,
    );

    // Set up debug logging for the shopify and checkout extensions
    mountRedoDebugFunction(storefrontClient);

    reportTiming("coverage.beforeCartCalls");

    if (selectedTreatment) {
      const treatmentId = applyTreatment({
        treatment: selectedTreatment,
        autocheck,
        fees,
      });
      setLocalStorageWithExpiry("redo_treatment_id", treatmentId, 0.5);
    }

    const storageCartToken = getSessionStorageWithExpiry(
      REDO_CART_TOKEN_STORAGE_KEY,
    );
    let cartToken: string | undefined =
      storageCartToken || globalThis?.Shopify?.Checkout?.cartToken;

    if (
      globalThis.redoStorefront?.type !== "commercecloud" &&
      globalThis.redoStorefront?.type !== "custom-lightopia"
    ) {
      if (!cartToken) {
        log("cart token cache missed!");
        const cart = await setInitialCartAttributes(
          storefrontClient,
          selectedTreatment,
          autocheck,
        );
        cartToken = cart.token;
      } else {
        // don't await to save time
        log("cart token cache Hit!", cartToken);
        void setInitialCartAttributes(
          storefrontClient,
          selectedTreatment,
          autocheck,
        );
      }
    }

    const shop =
      globalThis.Shopify?.shop ??
      globalThis.Shopify?.Checkout?.apiHost ??
      globalThis.redoStorefront?.storeUrl;
    let shopifyStorefrontGraphqlClient: StorefrontClient | undefined =
      undefined;
    let storefrontTestPromise;
    if (
      !onTapcart &&
      cartToken &&
      shop &&
      getStorefrontAccessToken(autocheck) &&
      cartToken.includes("?")
    ) {
      shopifyStorefrontGraphqlClient = new StorefrontClient(
        shop,
        getStorefrontAccessToken(autocheck),
        cartToken,
        getFreshCartToken,
      );
      storefrontTestPromise = shopifyStorefrontGraphqlClient.cartGet();
    }
    reportTiming("coverage.afterCartCalls");
    if (onTapcart) {
      // In the tapcart integration the cart is the best place to show our toggle so we override that setting
      autocheck.attachmentStrategy = AttachmentStrategy.SINGLE_TOGGLE;
    }

    // In preview mode, bypass certain restrictions
    if (!onPreview) {
      if (
        getThemePublishedStatus() !== ThemePublishedStatus.DRAFT &&
        autocheck.billing?.status &&
        showCoverageBasedOnBillingStatus[autocheck.billing.status] === false &&
        !inCoverageBuilderMode()
      ) {
        log(
          "⛔️ Redo storefront coverage disabled during bootstrap - merchant billing info is incomplete",
        );
        if (globalThis.redoStorefront) {
          globalThis.redoStorefront.coverageNotShowingReason =
            CoverageNotShowingReason.MERCHANT_BILLING_INFO_INCOMPLETE;
        }
        return;
      } else if (getThemePublishedStatus() === ThemePublishedStatus.DRAFT) {
        log("🟠 Ignoring billing status check - theme is draft");
      }
      if (autocheck.disableRedoForCustomer) {
        log(
          "⛔️ Redo storefront coverage disabled during bootstrap - disabled for customer",
        );
      }
      if (
        !autocheck.enabled ||
        !fees ||
        !fees.handle ||
        // The merchant's coverage settings stay enabled while Redo staff have
        // coverage paused, so the window has to bail out of bootstrap itself.
        isCoveragePausedAt(autocheck.coveragePausedDates) ||
        (!autocheck.coverageEnabled &&
          !autocheck.finalSaleReturns?.coverageEnabled &&
          !autocheck.packageProtection?.coverageEnabled)
      ) {
        if (globalThis.redoStorefront) {
          if (autocheck.disableRedoForCustomer) {
            globalThis.redoStorefront.coverageNotShowingReason =
              CoverageNotShowingReason.SHOPPER_NOT_ELIGIBLE;
          } else if (!fees || !fees.handle) {
            globalThis.redoStorefront.coverageNotShowingReason =
              CoverageNotShowingReason.FEES_ERROR;
          } else if (isCoveragePausedAt(autocheck.coveragePausedDates)) {
            globalThis.redoStorefront.coverageNotShowingReason =
              CoverageNotShowingReason.COVERAGE_PAUSED;
          } else if (
            (!autocheck.coverageEnabled &&
              !autocheck.finalSaleReturns?.coverageEnabled &&
              !autocheck.packageProtection?.coverageEnabled) ||
            !autocheck.enabled
          ) {
            globalThis.redoStorefront.coverageNotShowingReason =
              CoverageNotShowingReason.ALL_COVERAGE_DISABLED;
          }
        }
        log(
          "⛔️ Redo storefront coverage disabled during bootstrap - no coverage is enabled",
        );
        return;
      }
    }

    reportTiming("coverage.beforeGettingVariants");

    const shopifyProducts: ShopifyProducts =
      globalThis.redoStorefront?.previewShopifyProducts ??
      (await getRedoProductsCache({ autocheck, fees, storefrontClient }));

    reportTiming("coverage.afterGettingVariants");
    let canUseStorefrontAPI = false;
    if (
      shopifyStorefrontGraphqlClient &&
      storefrontTestPromise &&
      !autocheck.shouldNotUseStorefrontApi
    ) {
      try {
        const storageVal = getSessionStorageWithExpiry(
          "redo_can_use_storefront_api",
        );

        if (storageVal != null) {
          canUseStorefrontAPI = storageVal;
        } else {
          canUseStorefrontAPI = !!(await storefrontTestPromise);
          setSessionStorageWithExpiry(
            "redo_can_use_storefront_api",
            canUseStorefrontAPI,
          );
        }
      } catch {
        log("storefront client failed");
      }
    }

    log(`Can use storefront api? ${canUseStorefrontAPI}`);

    const cartEnabled =
      autocheck.cartToggle?.enabled ||
      doesAttachmentStrategyRenderInCart[autocheck.attachmentStrategy] ||
      location.search.includes("redo-cart=true") ||
      sessionStorage.getItem("redo-cart") === "true" ||
      onTapcart; // Tapcart always has cart enabled

    if (!cartEnabled && globalThis.redoStorefront) {
      globalThis.redoStorefront.coverageNotShowingReason =
        CoverageNotShowingReason.CHECKOUT_ONLY_STRATEGY;
    }

    if (autocheck.paidModel === "merchant_paid" && globalThis.redoStorefront) {
      globalThis.redoStorefront.coverageNotShowingReason =
        CoverageNotShowingReason.MERCHANT_PAID;
    }

    reportTiming("coverage.beforeInitialize");
    const { initialize } = await import("./manager");
    initialize(
      storefrontClient,
      shopifyStorefrontGraphqlClient,
      autocheck,
      cartEnabled,
      fees,
      shopifyProducts,
      canUseStorefrontAPI,
    );
    reportTiming("coverage.initializeFunctionCalled");
  });

  async function ensureMarketingAnonymousIdPresent() {
    const autocheck = await getAutocheckCache();

    const marketingEnabled = autocheck.marketing.enabled;
    const shouldRecordPackageProtectionExposure =
      !!autocheck.packageProtection?.shouldRecordPackageProtectionExposure;
    const existingAnonymousShopperId = localStorage.getItem(
      ANONYMOUS_SHOPPER_ID_KEY,
    );
    const idToUse = existingAnonymousShopperId ?? crypto.randomUUID();
    if (!existingAnonymousShopperId) {
      localStorage.setItem(ANONYMOUS_SHOPPER_ID_KEY, idToUse);
    }

    const existingShopifySessionId = localStorage.getItem(
      ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
    );
    const shopifySessionIdToUse =
      getCookie(AllCookies.ShopifySessionId) ?? idToUse;
    if (
      shopifySessionIdToUse &&
      shopifySessionIdToUse !== existingShopifySessionId
    ) {
      localStorage.setItem(
        ANONYMOUS_SHOPIFY_SESSION_ID_KEY,
        shopifySessionIdToUse,
      );
    }

    const shouldSyncCartAttributes =
      marketingEnabled ||
      (autocheck.conversion.enabled &&
        autocheck.conversion.predictiveConversion.enabled) ||
      shouldRecordPackageProtectionExposure;

    if (shouldSyncCartAttributes) {
      try {
        const shop =
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost ??
          globalThis.redoStorefront?.storeUrl;
        const storefrontAccessToken = getStorefrontAccessToken(autocheck);

        let cartToken: string | undefined =
          globalThis?.Shopify?.Checkout?.cartToken ||
          getSessionStorageWithExpiry(REDO_CART_TOKEN_STORAGE_KEY);

        if (!cartToken) {
          const ajaxClient = ShopifyAjaxClient.fromEnv();
          const ajaxCart = await ajaxClient.cartGet();
          cartToken = ajaxCart.token;
        }

        if (!shop || !cartToken) {
          log(
            "Shop or cart token not found, cannot sync anonymous ID to cart attributes",
          );
          return;
        }

        const storefrontClient = new StorefrontClient(
          shop,
          storefrontAccessToken,
          cartToken,
          getFreshCartToken,
        );

        const cart = await storefrontClient.cartGet();

        const attributesToUpdate: { key: string; value: string }[] = [];

        if (cart?.attributes?.[ANONYMOUS_ID_CART_ATTRIBUTE_KEY] !== idToUse) {
          attributesToUpdate.push({
            key: ANONYMOUS_ID_CART_ATTRIBUTE_KEY,
            value: idToUse,
          });
        }

        if (
          cart?.attributes?.[
            ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY
          ] !== shopifySessionIdToUse
        ) {
          attributesToUpdate.push({
            key: ANONYMOUS_SHOPIFY_SESSION_ID_CART_ATTRIBUTE_KEY,
            value: shopifySessionIdToUse ?? "",
          });
        }

        if (attributesToUpdate.length > 0) {
          await storefrontClient.updateCartAttribute({
            attributes: attributesToUpdate,
          });
        }
      } catch (error) {
        log("Error syncing anonymous ID to cart attribute", error);
      }
    }
  }
  void executeAndMonitor(async () =>
    ensureMarketingAnonymousIdPresent().catch(console.error),
  );

  function getCustomerEmailAndPhone() {
    const queryParameters = new URLSearchParams(location.search);
    const contactData = queryParameters.get("utm_contact");
    if (!contactData) {
      return;
    }

    const { email, phone } = contactDataFormat.read(decodeURI(contactData));

    if (email) {
      localStorage.setItem(CUSTOMER_EMAIL, email);
    }
    if (phone) {
      localStorage.setItem(CUSTOMER_PHONE, phone);
    }
  }
  void executeAndMonitor(async () => getCustomerEmailAndPhone());

  void executeAndMonitor(async () => {
    if (shouldShowPreviewModeUi()) {
      try {
        const { initializePreviewModeUi } =
          await import("./preview-mode/initializer");
        void initializePreviewModeUi();
      } catch (error) {
        console.warn("Error initializing Redo preview mode UI", error);
      }
    }
  });

  void executeAndMonitor(async () => {
    try {
      if (globalThis.blockRedo) {
        return;
      }
      const autocheck = await getAutocheckCache();
      if (
        !(
          globalThis.redoStorefront?.type === "bigcommerce" ||
          globalThis.redoStorefront?.type === "commercecloud" ||
          globalThis.redoStorefront?.type === "custom-lightopia"
        )
      ) {
        log(
          "Not on BigCommerce or Commerce cloud so event tracking will be through the web pixel (or not at all)",
        );
        return;
      }

      const { initialize: initializeStorefrontTracking } =
        await import("@redotech/redo-storefront-tracking/index");

      const storefrontTrackingConfig = getStorefrontTrackingConfigFromAutocheck(
        { autocheck },
      );

      await initializeStorefrontTracking({
        config: storefrontTrackingConfig,
        analyticsProvider: new BasicEventsProvider(storefrontTrackingConfig),
        location: window.location,
      });

      log("Storefront tracking initialized");
    } catch (error) {
      console.error("Error initializing storefront tracking", error);
    }
  });
};

// If you see this log, check that you don't have multiple Redo app installations
// with their embeds enabled in the theme editor.
if (globalThis.redo_app_loaded_on_page) {
  console.log("Redo already loaded, skipping re-initialization");
} else {
  globalThis.redo_app_loaded_on_page = true;
  void initializeRedoStorefrontSubmodules();
}
